import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { useAuth } from '../features/auth/AuthProvider';
import { LoginPage } from '../features/auth/LoginPage';
import { SetupPage } from '../features/auth/SetupPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { EmailPage } from '../features/email/EmailPage';
import { CollectionPage } from '../features/entities/CollectionPage';
import { AdminPage } from '../features/entities/AdminPage';
import { PowerDialerPage } from '../features/phone/PowerDialerPage';
import { MapPage } from '../features/map/MapPage';
import { ProfilePage } from '../features/profile/ProfilePage';
import { PipelinesPage } from '../features/pipelines/PipelinesPage';
import { LeadsPage } from '../features/leads/LeadsPage';
import { CampaignsPage } from '../features/campaigns/CampaignsPage';
import { InboxPage } from '../features/inbox/InboxPage';
import { CallListPage } from '../features/calls/CallListPage';

function AppGate() {
	const { isReady, isAuthenticated, needsSetup } = useAuth();
	const location = useLocation();

	if (!isReady) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-canvas text-secondary">
				<div className="rounded-3xl border border-subtle bg-surface-2 px-6 py-4 shadow-panel">Loading workspace...</div>
			</div>
		);
	}

	if (needsSetup && location.pathname !== '/setup') {
		return <Navigate to="/setup" replace />;
	}

	if (!isAuthenticated) {
		return <Navigate to="/auth/login" replace state={{ from: location }} />;
	}

	return <AppShell />;
}

function PublicOnly() {
	const { isReady, isAuthenticated, needsSetup } = useAuth();

	if (!isReady) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-canvas text-secondary">
				<div className="rounded-3xl border border-subtle bg-surface-2 px-6 py-4 shadow-panel">Loading workspace...</div>
			</div>
		);
	}

	if (needsSetup) {
		return <Navigate to="/setup" replace />;
	}

	if (isAuthenticated) {
		return <Navigate to="/today" replace />;
	}

	return <Outlet />;
}

export const router = createBrowserRouter([
	{
		path: '/auth',
		element: <PublicOnly />,
		children: [
			{ path: 'login', element: <LoginPage /> }
		]
	},
	{
		path: '/setup',
		element: <SetupPage />
	},
	{
		path: '/',
		element: <AppGate />,
		children: [
			{ index: true, element: <Navigate to="/today" replace /> },
			{ path: 'today', element: <DashboardPage /> },
			{ path: 'dashboard', element: <Navigate to="/today" replace /> },
			{ path: 'inbox', element: <InboxPage /> },
			{ path: 'leads', element: <LeadsPage /> },
			{ path: 'pipelines', element: <PipelinesPage /> },
			{ path: 'opportunities', element: <Navigate to="/pipelines" replace /> },
			{ path: 'companies', element: <CollectionPage title="Companies" resource="companies" /> },
			{ path: 'contacts', element: <CollectionPage title="Contacts" resource="contacts" /> },
			{ path: 'activities', element: <CollectionPage title="Activities" resource="activities" /> },
			{ path: 'emails', element: <EmailPage /> },
			{ path: 'calls', element: <CallListPage /> },
			{ path: 'dialer', element: <PowerDialerPage /> },
			{ path: 'campaigns', element: <CampaignsPage /> },
			{ path: 'map', element: <MapPage /> },
			{ path: 'admin', element: <AdminPage /> },
			{ path: 'admin/system', element: <AdminPage /> },
			{ path: 'admin/settings', element: <AdminPage /> },
			{ path: 'profile', element: <ProfilePage /> }
		]
	},
	{
		path: '*',
		element: <Navigate to="/" replace />
	}
]);
